"use client";

import { useAuthUserContext } from "@/contexts/authUserContext";
import { numberToString } from "@/lib/js_helpers";
import { ErrorResponse, NewsCardMeta, UserMentionsResponse, UserProfileResponse, UserRepliesResponse } from "@/lib/types";
import { ChartBarIcon, Squares2X2Icon, TagIcon, VideoCameraIcon } from "@heroicons/react/16/solid";
import clsx from "clsx";
import Link from "next/link";
import { useParams } from "next/navigation";
import React, { useState } from "react";
import Image from "next/image";

type ContentTab = 'posts' | 'mentions' | 'reply' | 'analytics';

function UserContentMenuButton({Icon, count, isActive, onClick}: {
    Icon: React.ForwardRefExoticComponent<Omit<React.SVGProps<SVGSVGElement>, "ref"> & 
    {
        title?: string;
        titleId?: string;
    } & React.RefAttributes<SVGSVGElement>>, 
    count: string, 
    isActive: boolean, 
    onClick: () => void
}) {
    const {user} = useAuthUserContext();
    const params = useParams();

    const isCurrentAuthUser = user?.uid === params.user_id;

    return (
        <button
            className={clsx('flex flex-col items-center min-w-6 cursor-pointer hover:bg-slate-200 p-5', isCurrentAuthUser ? 'w-1/4' : 'w-1/3')}
            onClick={onClick}
        >
            <Icon className={`size-6 ${isActive ? 'text-[#D10846]' : 'text-[#B3B3B3]'}`} />
            <span className="text-sm font-normal">{count}</span>
        </button>
    );
}

function UserContentMenu({userProfile, userMentions, userReplies, userAnalytics, currentTab, setCurrentTab}: {
    currentTab: ContentTab, 
    setCurrentTab: (tab: ContentTab) => void,
    userProfile: UserProfileResponse | ErrorResponse,
    userMentions: UserMentionsResponse | ErrorResponse,
    userReplies: UserRepliesResponse | ErrorResponse,
    userAnalytics: any | ErrorResponse,
}) {

    const {user} = useAuthUserContext();
    const params = useParams();

    const isCurrentAuthUser = user?.uid === params.user_id;

    return (
        <div className="flex justify-around w-full max-w-2xl mx-auto border border-slate-200 mt-5">
            <UserContentMenuButton count={numberToString((!('error' in userProfile) && userProfile.user_info?.post_count) || 0)} Icon={Squares2X2Icon} isActive={currentTab === 'posts'} onClick={() => setCurrentTab('posts')} />
            <UserContentMenuButton count={numberToString((!('error' in userMentions) && userMentions.user_info?.mentions_count) || 0)} Icon={TagIcon} isActive={currentTab === 'mentions'} onClick={() => setCurrentTab('mentions')} />
            <UserContentMenuButton count={numberToString((!('error' in userReplies) && userReplies.user_info?.reply_count) || 0)} Icon={VideoCameraIcon} isActive={currentTab === 'reply'} onClick={() => setCurrentTab('reply')} />
            {isCurrentAuthUser && <UserContentMenuButton count={userAnalytics.cumulative_data?.average_views ? numberToString(userAnalytics.cumulative_data.average_views) : '-'} Icon={ChartBarIcon} isActive={currentTab === 'analytics'} onClick={() => setCurrentTab('analytics')} />}
        </div>
    );
}

function UserContentList({listItems}: {listItems: NewsCardMeta[]}){
    if(!listItems.length) return <div className="text-center my-2.5"><span>No Items</span></div>
    return (
        <div className="grid grid-cols-3 gap-0.5 w-full margin-auto box-border">
            {listItems.map((post, i) => (
                <div key={post.id} className="relative aspect-[1/1] box-border">
                    <Link href={`/video/${post.id}`} target="_blank" >
                        <Image src={post.card_obj?.thumbnail_url as string} width={200} height={200} alt={post.card_obj?.title || `Video ${i}`} className="w-full h-full object-cover" />
                    </Link>
                </div>
            ))}
        </div>
    )
}

export default function UserContent({userProfile, userMentions, userReplies, userAnalytics}: {
    userProfile: UserProfileResponse | ErrorResponse, 
    userMentions: UserMentionsResponse | ErrorResponse,
    userReplies: UserRepliesResponse | ErrorResponse,
    userAnalytics?: any | ErrorResponse,
}) {
    const [currentTab, setCurrentTab] = useState<ContentTab>('posts');
    let listItems: NewsCardMeta[] = [];

    switch(currentTab) {
        case 'posts':
            if(!('error' in userProfile)) {
                listItems = userProfile.card_list || [];
            }
            break;
        case 'mentions':
            if(!('error' in userMentions)) {
                listItems = userMentions.card_list || [];
            }
            break;
        case 'reply':
            if(!('error' in userReplies)) {
                listItems = userReplies.card_list || [];
            }
            break;
        case 'analytics':
            listItems = [];
            break;
        default:
            listItems = [];
    }

    return (
        <>
            <UserContentMenu 
                userProfile={userProfile} 
                userMentions={userMentions} 
                userReplies={userReplies}
                userAnalytics={userAnalytics}
                currentTab={currentTab} 
                setCurrentTab={setCurrentTab} 
            />
            <UserContentList listItems={listItems} />
        </>
    );
}

// TODO: Add bottom_panel_clicks
// TODO: Add Analytics Tab Content